import { useState, useEffect, useCallback } from 'react';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';

// icons
import { Shield, Clock, UserSquare } from 'iconsax-react';

// services
import auditService from '../../services/auditService';

const RoleAssignmentHistory = ({ user }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchRoleHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch audit logs for role-related actions for this user
      const result = await auditService.getAuditLogs({
        userId: user._id,
        actions: ['ROLE_ASSIGNED', 'ROLE_UPDATED', 'USER_CREATED'],
        limit: 20
      });

      if (result.success) {
        const logs = result.data.logs || [];
        // Sort by timestamp descending (newest first)
        const sortedLogs = logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setHistory(sortedLogs);
      } else {
        setError('Failed to load role history');
      }
    } catch (error) {
      console.error('Error fetching role history:', error);
      setError('Failed to load role history');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchRoleHistory();
    }
  }, [user, fetchRoleHistory]);


  const getActionIcon = (action) => {
    switch (action) {
      case 'ROLE_ASSIGNED':
        return <Shield size={20} className="text-primary" />;
      case 'ROLE_UPDATED':
        return <Shield size={20} className="text-amber-500" />;
      case 'USER_CREATED':
        return <UserSquare size={20} className="text-emerald-600" />;
      default:
        return <Clock size={20} className="text-slate-400" />;
    }
  };

  const getActionBadgeClass = (action) => {
    switch (action) {
      case 'ROLE_ASSIGNED':
        return 'bg-blue-100 text-blue-700 hover:bg-blue-100 border-none';
      case 'ROLE_UPDATED':
        return 'bg-amber-100 text-amber-700 hover:bg-amber-100 border-none';
      case 'USER_CREATED':
        return 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none';
      default:
        return 'bg-slate-100 text-slate-700 hover:bg-slate-100 border-none';
    }
  };

  const formatActionDescription = (log) => {
    const details = log.details || {};
    
    switch (log.action) {
      case 'ROLE_ASSIGNED':
        return `Role changed from "${details.oldRole || 'None'}" to "${details.newRole || 'Unknown'}"`;
      case 'ROLE_UPDATED':
        return `Role "${details.roleName || 'Unknown'}" permissions updated`;
      case 'USER_CREATED':
        return `User account created with role "${details.role || user.role}"`;
      default:
        return log.action.replace(/_/g, ' ').toLowerCase();
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mt-4">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-6 text-sm text-muted-foreground">
        No role assignment history found for this user
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Recent role changes and assignments for {user.name}
      </p>

      <ul className="divide-y divide-slate-100">
        {history.map((log, index) => (
          <li key={log._id || index} className="flex items-start gap-3 py-4 first:pt-0 last:pb-0">
            <div className="mt-0.5 p-1 rounded-full bg-slate-50 shrink-0">
              {getActionIcon(log.action)}
            </div>
            <div className="flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-foreground">
                  {formatActionDescription(log)}
                </span>
                <Badge className={`text-[10px] font-semibold px-2 py-0.5 border-none ${getActionBadgeClass(log.action)}`}>
                  {log.action.replace(/_/g, ' ')}
                </Badge>
              </div>
              <div className="text-[11px] text-muted-foreground space-y-0.5">
                <p>{formatTimestamp(log.timestamp)}</p>
                {log.userAgent && (
                  <p>
                    IP: {log.ipAddress || 'Unknown'} • Browser: {log.userAgent?.split(' ')[0] || 'Unknown'}
                  </p>
                )}
                {log.details?.sessionsTerminated > 0 && (
                  <p className="text-amber-600 font-medium">
                    {log.details.sessionsTerminated} active session(s) terminated
                  </p>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RoleAssignmentHistory;